import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MentorshipCallout from '../components/MentorshipCallout';
import {
  MOCK_INTERVIEW_BOOKING_PRICE,
  MENTORSHIP_MONTHLY_PRICE,
  MENTORSHIP_SIX_MONTH_PRICE,
} from '../common/constants';

const ACCENT = '#137274';
const PINK = '#f50057';

const formatInr = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

const pathways = [
  {
    title: 'Practice Track',
    body: 'Structured DSA Level 1 & 2 — topics, progress rings, and a clear next problem.',
    to: '/practice-track',
    cta: 'Open track',
    icon: RouteOutlinedIcon,
    accent: ACCENT,
  },
  {
    title: 'Interview Prep',
    body: 'System Design, LLD, CS Fundamentals, and Behavioral — written for interview loops.',
    to: '/interview-prep',
    cta: 'Browse tracks',
    icon: MenuBookOutlinedIcon,
    accent: '#2563eb',
  },
  {
    title: 'Live Coding',
    body: 'Private room with synced editor, video, and shared run output for pair practice.',
    to: '/live-coding',
    cta: 'Start a room',
    icon: VideocamOutlinedIcon,
    accent: PINK,
  },
  {
    title: 'Contests',
    body: 'Timed rounds to build speed and nerves before the real interview clock.',
    to: '/contests',
    cta: 'View contests',
    icon: BoltOutlinedIcon,
    accent: '#d97706',
  },
];

const curriculumTopics = [
  'Arrays & Patterns',
  'Recursion & Backtracking',
  'Linked Lists',
  'Trees & BST',
  'Graphs',
  'Dynamic Programming',
  'HashMap & Heap',
  'System Design (HLD)',
  'LLD & OOP',
  'CS Fundamentals',
  'Behavioral / HM',
  'Resume & storytelling',
];

const AllProgramsPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark
    ? '#0f1419'
    : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 40%, #ffffff 100%)';

  return (
    <Box sx={{ minHeight: '100vh', background: pageBg }}>
      <Helmet>
        <title>Programs · Practice, Interview Prep & 1:1 Mentorship | CrafterHack</title>
        <meta
          name="description"
          content="CrafterHack programs — Practice Track, Interview Prep, Live Coding, Contests, and 1:1 Mentorship with a FAANG SDE."
        />
        <link rel="canonical" href="https://crafterhack.com/programs" />
      </Helmet>

      {/* Hero */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 4, md: 6 },
          pb: { xs: 4.5, md: 6 },
          borderBottom: `1px solid ${border}`,
          overflow: 'hidden',
          background: isDark
            ? `radial-gradient(900px 400px at 12% -10%, ${alpha(ACCENT, 0.4)}, transparent 55%), radial-gradient(600px 300px at 95% 0%, ${alpha(PINK, 0.12)}, transparent 50%), #0f1419`
            : `radial-gradient(900px 400px at 10% -20%, ${alpha(ACCENT, 0.18)}, transparent 55%), radial-gradient(600px 300px at 95% 0%, ${alpha(PINK, 0.08)}, transparent 50%), linear-gradient(180deg, #e8f4f5 0%, #f7f9fb 100%)`,
        }}
      >
        <Box sx={{ maxWidth: 1040, mx: 'auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Chip
              icon={<CodeOutlinedIcon sx={{ fontSize: '16px !important' }} />}
              label="Programs"
              sx={{
                mb: 1.75,
                fontWeight: 800,
                bgcolor: alpha(ACCENT, isDark ? 0.28 : 0.14),
                color: ACCENT,
                '& .MuiChip-icon': { color: ACCENT },
              }}
            />
            <Typography
              component="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2rem', sm: '2.55rem', md: '2.85rem' },
                letterSpacing: '-0.04em',
                lineHeight: 1.08,
                mb: 1.25,
                maxWidth: 640,
              }}
            >
              Everything you need to get{' '}
              <Box component="span" sx={{ color: ACCENT }}>
                interview-ready
              </Box>
            </Typography>
            <Typography
              sx={{
                color: muted,
                fontSize: { xs: '1rem', sm: '1.08rem' },
                lineHeight: 1.55,
                maxWidth: 540,
                mb: 2.75,
              }}
            >
              Self-paced Practice Track and Interview Prep — or go deep with 1:1 Mentorship from a
              FAANG SDE. Pick the lane that matches your timeline.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
              <Button
                component={Link}
                to="/mentorship"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: ACCENT,
                  px: 2.5,
                  '&:hover': { bgcolor: '#0f5c5e' },
                }}
              >
                Explore 1:1 Mentorship
              </Button>
              <Button
                component={Link}
                to="/practice-track"
                variant="outlined"
                size="large"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: border,
                  color: 'inherit',
                  bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#fff', 0.7),
                }}
              >
                Open Practice Track
              </Button>
            </Box>
          </motion.div>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1040, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 4, md: 5 } }}>
        {/* Featured mentorship */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
            <WorkspacePremiumOutlinedIcon sx={{ color: ACCENT, fontSize: 22 }} />
            <Typography sx={{ fontWeight: 900, fontSize: '1.35rem', letterSpacing: '-0.03em' }}>
              Featured · 1:1 Mentorship
            </Typography>
          </Box>
          <MentorshipCallout variant="banner" />
        </Box>

        {/* Mentorship pricing strip */}
        <Box
          sx={{
            mb: 5,
            mt: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}
        >
          {[
            {
              label: 'Mock interview',
              price: formatInr(MOCK_INTERVIEW_BOOKING_PRICE),
              detail: 'Pick a slot · DSA / design / behavioral',
            },
            {
              label: 'Monthly',
              price: `${formatInr(MENTORSHIP_MONTHLY_PRICE)}/mo`,
              detail: 'Daily connect · DSA + SD + CS',
            },
            {
              label: '6–7 month plan',
              price: formatInr(MENTORSHIP_SIX_MONTH_PRICE),
              detail: 'Full planner · referrals included',
              highlight: true,
            },
          ].map((p) => (
            <Box
              key={p.label}
              component={Link}
              to="/mentorship"
              sx={{
                p: 2,
                borderRadius: 2.5,
                textDecoration: 'none',
                color: 'inherit',
                border: `1px solid ${p.highlight ? ACCENT : border}`,
                bgcolor: surface,
                background: p.highlight
                  ? isDark
                    ? `linear-gradient(160deg, ${alpha(ACCENT, 0.22)}, ${surface})`
                    : `linear-gradient(160deg, ${alpha(ACCENT, 0.1)}, #fff)`
                  : surface,
                transition: 'transform 0.15s',
                '&:hover': { transform: 'translateY(-2px)' },
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  color: p.highlight ? ACCENT : muted,
                  mb: 0.5,
                }}
              >
                {p.label}
              </Typography>
              <Typography sx={{ fontWeight: 900, fontSize: '1.25rem', letterSpacing: '-0.03em', mb: 0.35 }}>
                {p.price}
              </Typography>
              <Typography variant="body2" sx={{ color: muted, fontSize: '0.8rem' }}>
                {p.detail}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Self-serve pathways */}
        <Box sx={{ mb: 5 }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.3rem', sm: '1.5rem' },
              letterSpacing: '-0.03em',
              mb: 0.75,
            }}
          >
            Self-serve programs
          </Typography>
          <Typography sx={{ color: muted, mb: 2.5, maxWidth: 520, fontSize: '0.95rem' }}>
            Free to explore anytime — pair with mentorship when you want accountability.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.75,
            }}
          >
            {pathways.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.05, 0.25) }}
                >
                  <Box
                    component={Link}
                    to={p.to}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      p: 2.5,
                      borderRadius: 2.5,
                      textDecoration: 'none',
                      color: 'inherit',
                      border: `1px solid ${border}`,
                      bgcolor: surface,
                      transition: 'border-color 0.15s, transform 0.15s',
                      '&:hover': {
                        borderColor: alpha(p.accent, 0.5),
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 1.75,
                        display: 'grid',
                        placeItems: 'center',
                        mb: 1.5,
                        bgcolor: alpha(p.accent, isDark ? 0.22 : 0.12),
                        color: p.accent,
                      }}
                    >
                      <Icon sx={{ fontSize: 22 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 0.5 }}>
                      {p.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: muted, flex: 1, fontSize: '0.88rem', lineHeight: 1.5, mb: 1.75 }}
                    >
                      {p.body}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '0.85rem',
                        color: p.accent,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {p.cta} <ArrowForwardIcon sx={{ fontSize: 16 }} />
                    </Typography>
                  </Box>
                </motion.div>
              );
            })}
          </Box>
        </Box>

        {/* Curriculum coverage */}
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.3rem', sm: '1.5rem' },
              letterSpacing: '-0.03em',
              mb: 0.75,
            }}
          >
            What you’ll cover
          </Typography>
          <Typography sx={{ color: muted, mb: 2.5, maxWidth: 520, fontSize: '0.95rem' }}>
            Across Practice Track, Interview Prep, and 1:1 Mentorship — from DSA patterns to design
            and resume polish.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr 1fr',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 1.25,
            }}
          >
            {curriculumTopics.map((topic) => (
              <Box
                key={topic}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.85,
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${border}`,
                  bgcolor: isDark ? alpha('#fff', 0.02) : alpha(ACCENT, 0.03),
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 16, color: ACCENT, mt: 0.2, flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.35 }}>
                  {topic}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Closing CTA */}
        <Box
          sx={{
            p: { xs: 2.5, sm: 3.25 },
            borderRadius: 3,
            border: `1px solid ${alpha(ACCENT, 0.3)}`,
            textAlign: { xs: 'left', sm: 'center' },
            background: isDark
              ? `linear-gradient(120deg, ${alpha(ACCENT, 0.2)}, ${alpha(PINK, 0.08)})`
              : `linear-gradient(120deg, ${alpha(ACCENT, 0.1)}, ${alpha(PINK, 0.05)})`,
          }}
        >
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.25rem', sm: '1.45rem' },
              letterSpacing: '-0.03em',
              mb: 0.75,
            }}
          >
            Ready for accountability?
          </Typography>
          <Typography
            sx={{ color: muted, mb: 2.25, maxWidth: 480, mx: { sm: 'auto' }, fontSize: '0.92rem' }}
          >
            Start free on Practice Track — or lock a 1:1 Mentorship plan with daily connect and a
            6–7 month interview planner.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              flexWrap: 'wrap',
              justifyContent: { sm: 'center' },
            }}
          >
            <Button
              component={Link}
              to="/mentorship"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                bgcolor: ACCENT,
                '&:hover': { bgcolor: '#0f5c5e' },
              }}
            >
              1:1 Mentorship
            </Button>
            <Button
              component={Link}
              to="/practice-track"
              variant="outlined"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderColor: border,
                color: 'inherit',
              }}
            >
              Practice Track
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AllProgramsPage;
